import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

const NAV_LINKS = [
  { label: "NEWS · 新闻", href: "#news" },
  { label: "PROJECTS · 作品", href: "#projects" },
  { label: "ABOUT · 关于", href: "#about" },
  { label: "CONTACT · 联系", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const sections = ["news", "projects", "about", "contact"];
    const observers: IntersectionObserver[] = [];

    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id);
        },
        { threshold: 0.3 }
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const handleNavClick = useCallback((href: string) => {
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery("");
    setSearchOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="w-full px-8 lg:px-16">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <h1 className="shrink-0 m-0 p-0 leading-none">
            <Link to="/" className="flex items-center">
              <img
                src="https://static.readdy.ai/image/c8608e961562513b854fa363830f8188/28b3cd5f3d1d3a04a9ae79d23e74780b.png"
                alt="YEAHSTUDIO 隐也设计工作室 — 上海建筑与室内设计事务所，专注零售设计、品牌空间、商业设计、酒店住宅及建筑空间设计"
                className="h-10 w-auto object-contain"
              />
              <span className="sr-only">YEAHSTUDIO 隐也设计工作室 — 上海建筑与室内设计事务所，专注零售设计、品牌空间设计、商业设计、酒店餐饮住宅设计及建筑空间设计</span>
            </Link>
          </h1>

          {/* Nav Links */}
          <nav className="hidden lg:flex items-center gap-10">
            {NAV_LINKS.map((link) => {
              const id = link.href.replace("#", "");
              const isActive = activeSection === id;
              return (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link.href)}
                  className={`relative text-xs font-medium tracking-[0.2em] transition-colors duration-300 cursor-pointer whitespace-nowrap pb-1 ${
                    scrolled
                      ? isActive
                        ? "text-stone-900"
                        : "text-stone-500 hover:text-stone-900"
                      : isActive
                      ? "text-white"
                      : "text-white/70 hover:text-white"
                  }`}
                >
                  {link.label}
                  <span
                    className={`absolute bottom-0 left-0 h-px bg-current transition-all duration-300 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </button>
              );
            })}
          </nav>

          {/* Right: Search */}
          <div className="flex items-center gap-4">
            {searchOpen ? (
              <form onSubmit={handleSearchSubmit} className="flex items-center">
                <input
                  autoFocus
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索 / Search..."
                  className={`text-sm w-48 border-b bg-transparent outline-none px-2 py-1 transition-colors duration-300 ${
                    scrolled
                      ? "border-stone-400 text-stone-900 placeholder-stone-400"
                      : "border-white/60 text-white placeholder-white/60"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setSearchOpen(false)}
                  className={`ml-2 transition-colors duration-300 cursor-pointer w-6 h-6 flex items-center justify-center ${
                    scrolled ? "text-stone-500 hover:text-stone-900" : "text-white/70 hover:text-white"
                  }`}
                >
                  <i className="ri-close-line text-base" />
                </button>
              </form>
            ) : (
              <button
                onClick={() => setSearchOpen(true)}
                className={`transition-colors duration-300 cursor-pointer w-8 h-8 flex items-center justify-center ${
                  scrolled ? "text-stone-500 hover:text-stone-900" : "text-white/70 hover:text-white"
                }`}
              >
                <i className="ri-search-line text-lg" />
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
